/**
 * Order → Progress reports tab (mockup docs/design/marketplace-hire/ProgressReport.dc.html).
 * The builder writes a short report; Twasta attaches what it MEASURED in the
 * project. The client marks the builder's needs done and acknowledges it.
 */
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { fmtDate, mktError } from '@/api/marketplace'
import {
  acknowledgeReport, measureOrder, sendReport, toggleReportNeed,
  type Measured, type ProgressReport,
} from '@/api/marketplaceBuild'
import { MeasuredBlock, firstName, fmtDateTime, type OrderCtx } from './orderShared'

const RAG: Record<ProgressReport['rag'], { label: string; hint: string; tone: string }> = {
  green: { label: 'On track', hint: 'No change to dates', tone: 'ok' },
  amber: { label: 'At risk', hint: 'Might slip; say why', tone: 'warn' },
  red: { label: 'Late', hint: 'A date will move', tone: 'bad' },
}
const RISK_TONE: Record<'low' | 'medium' | 'high', string> = { low: '', medium: 'warn', high: 'bad' }
const RISK_LABEL: Record<'low' | 'medium' | 'high', string> = { low: 'Low', medium: 'Medium', high: 'High' }

export default function OrderReportsTab(ctx: OrderCtx) {
  const { order } = ctx
  const [sel, setSel] = useState<number | null>(order.reports[0]?.id ?? null)
  const [writing, setWriting] = useState(false)
  useEffect(() => {
    if (sel == null || !order.reports.some((r) => r.id === sel)) setSel(order.reports[0]?.id ?? null)
  }, [order.reports, sel])
  const report = order.reports.find((r) => r.id === sel) ?? null
  const replace = (r: ProgressReport) => ctx.setOrder({ ...order, reports: order.reports.map((x) => (x.id === r.id ? r : x)) })

  return (
    <div className="mkt-col" style={{ gap: 20 }}>
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <span className="mkt-muted">
          {order.role === 'builder'
            ? `Tell ${order.client.name} how it’s going. Twasta adds what it measured in your project.`
            : `${firstName(order.builder.person, order.builder.name)} sends a report as the work goes. Twasta adds what it measured in the project itself.`}
        </span>
        {order.can.report && !writing && <button type="button" className="btn btn-primary" onClick={() => setWriting(true)}>Write a progress report</button>}
      </div>

      {writing && <ReportForm ctx={ctx} onDone={async () => { setWriting(false); await ctx.reload() }} onCancel={() => setWriting(false)} />}

      {order.reports.length === 0 ? (
        !writing && (
          <div className="mkt-empty">
            <strong style={{ fontSize: 18 }}>No progress reports yet</strong>
            <span className="mkt-muted">{order.role === 'builder' ? 'Send the first one when the work starts.' : 'Reports appear here as the builder sends them.'}</span>
          </div>
        )
      ) : (
        <div className="mkt-split">
          <nav className="mkt-col" aria-label="Reports" style={{ flex: '1 1 220px', maxWidth: 280, gap: 6 }}>
            {order.reports.map((r) => (
              <button key={r.id} type="button" aria-current={r.id === sel ? 'page' : undefined} onClick={() => setSel(r.id)}
                className={`mkt-choice${r.id === sel ? ' on' : ''}`} style={{ minHeight: 0, padding: '10px 12px' }}>
                <span className="mkt-row" style={{ justifyContent: 'space-between', width: '100%' }}>
                  <strong>{r.period_label}</strong>
                  <span className={`mkt-pill ${RAG[r.rag].tone}`}>{RAG[r.rag].label}</span>
                </span>
                <span className="mkt-faint">Sent {fmtDate(r.sent_at)}{r.acknowledged_at ? ' · read' : ''}</span>
              </button>
            ))}
          </nav>
          {report && <ReportView ctx={ctx} report={report} onChanged={replace} />}
        </div>
      )}
    </div>
  )
}

function ReportView({ ctx, report, onChanged }: { ctx: OrderCtx; report: ProgressReport; onChanged: (r: ProgressReport) => void }) {
  const { order } = ctx
  const client = order.role === 'client'
  const [busy, setBusy] = useState(false)
  const builderFirst = firstName(order.builder.person, order.builder.name)
  const run = async (fn: () => Promise<ProgressReport>, ok: string) => {
    setBusy(true)
    try { onChanged(await fn()); toast.success(ok) } catch (e) { toast.error(mktError(e).message) } finally { setBusy(false) }
  }
  const openNeeds = report.needs.filter((n) => !n.done).length

  return (
    <article className="mkt-main mkt-panel" style={{ gap: 18 }} aria-label={`Report ${report.period_label}`}>
      <div className="mkt-col" style={{ gap: 4 }}>
        <span className="mkt-faint">{report.period_label} · sent {fmtDateTime(report.sent_at)}</span>
        <div className="mkt-row" style={{ gap: 10 }}>
          <span className={`mkt-pill ${RAG[report.rag].tone}`}>● {RAG[report.rag].label}</span>
          <h2>{report.summary || 'Progress report'}</h2>
        </div>
      </div>

      <MeasuredBlock m={report.measured} />

      <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
        <div className="mkt-col" style={{ gap: 6 }}>
          <strong>Done</strong>
          {report.done.length ? (
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {report.done.map((d, i) => <li key={i}><span aria-hidden="true" style={{ color: 'var(--color-success)' }}>✓ </span>{d}</li>)}
            </ul>
          ) : <span className="mkt-faint">Nothing listed.</span>}
        </div>
        <div className="mkt-col" style={{ gap: 6 }}>
          <strong>Next</strong>
          {report.next.length ? (
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {report.next.map((d, i) => <li key={i}><span aria-hidden="true" className="mkt-muted">→ </span>{d}</li>)}
            </ul>
          ) : <span className="mkt-faint">Nothing listed.</span>}
        </div>
      </div>

      {report.needs.length > 0 && (
        <div className="mkt-col" style={{ gap: 8 }}>
          <strong>{client ? `${builderFirst} needs from you` : `Needed from ${order.client.name}`} <span className="mkt-faint">· {openNeeds} open</span></strong>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {report.needs.map((n, i) => (
              <li key={i} className="mkt-row" style={{ justifyContent: 'space-between', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--color-border)', background: n.done ? 'var(--color-bg)' : 'var(--color-bg-secondary)' }}>
                <span className="mkt-col" style={{ gap: 0, flex: '1 1 240px' }}>
                  <span style={n.done ? { textDecoration: 'line-through', color: 'var(--color-text-secondary)' } : undefined}>{n.text}</span>
                  {n.due && <span className="mkt-faint">Needed by {fmtDate(n.due)}</span>}
                </span>
                {client ? (
                  <button type="button" className={`btn btn-sm ${n.done ? 'btn-ghost' : 'btn-secondary'}`} aria-pressed={n.done} disabled={busy}
                    onClick={() => run(() => toggleReportNeed(order.id, report.id, i), n.done ? 'Marked as open again' : 'Marked as done')}>
                    {n.done ? '✓ Done — undo' : 'Mark as done'}
                  </button>
                ) : <span className={`mkt-pill ${n.done ? 'ok' : ''}`}>{n.done ? 'Done' : 'Open'}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.risks.length > 0 && (
        <div className="mkt-col" style={{ gap: 8 }}>
          <strong>Risks</strong>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {report.risks.map((r, i) => (
              <li key={i} className="mkt-row" style={{ gap: 10, flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                <span className={`mkt-pill ${RISK_TONE[r.level]}`}>{RISK_LABEL[r.level]}</span><span>{r.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mkt-row" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
        {report.acknowledged_at ? (
          <span className="mkt-muted" style={{ fontSize: 14 }}>✓ Read by {report.acknowledged_by || order.client.person} on {fmtDate(report.acknowledged_at)}</span>
        ) : client ? (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => run(() => acknowledgeReport(order.id, report.id), 'Report acknowledged')}>Acknowledge report</button>
        ) : <span className="mkt-faint">Not read by {order.client.name} yet.</span>}
        {client && order.preview_path && <a href={order.preview_path} target="_blank" rel="noopener" className="btn btn-secondary">Try the working copy</a>}
        <button type="button" className="btn btn-ghost" onClick={() => ctx.goTab('messages')}>Comment in messages</button>
      </div>
    </article>
  )
}

// ── builder: write a report ────────────────────────────────────────────────

interface NeedRow { text: string; due: string }
interface RiskRow { level: 'low' | 'medium' | 'high'; text: string }

function ListEditor({ id, label, items, onChange, placeholder }: { id: string; label: string; items: string[]; onChange: (v: string[]) => void; placeholder: string }) {
  return (
    <fieldset style={{ border: 0, margin: 0, padding: 0 }} className="mkt-col">
      <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{label}</legend>
      {items.map((v, i) => (
        <div key={i} className="mkt-row" style={{ flexWrap: 'nowrap', gap: 8 }}>
          <div className="mkt-field" style={{ flex: 1 }}>
            <label htmlFor={`${id}-${i}`} className="mkt-sr">{label} {i + 1}</label>
            <input id={`${id}-${i}`} value={v} placeholder={placeholder} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} />
          </div>
          <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove ${label.toLowerCase()} ${i + 1}`} onClick={() => onChange(items.filter((_, j) => j !== i))}>Remove</button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => onChange([...items, ''])}>+ Add</button>
    </fieldset>
  )
}

function ReportForm({ ctx, onDone, onCancel }: { ctx: OrderCtx; onDone: () => void; onCancel: () => void }) {
  const { order } = ctx
  const [rag, setRag] = useState<ProgressReport['rag']>('green')
  const [period, setPeriod] = useState('')
  const [summary, setSummary] = useState('')
  const [done, setDone] = useState<string[]>([''])
  const [next, setNext] = useState<string[]>([''])
  const [needs, setNeeds] = useState<NeedRow[]>([])
  const [risks, setRisks] = useState<RiskRow[]>([])
  const [measured, setMeasured] = useState<Measured | null>(null)
  const [measureErr, setMeasureErr] = useState<string | null>(null)
  const [measuring, setMeasuring] = useState(false)
  const [sending, setSending] = useState(false)

  const measure = useCallback(async () => {
    setMeasuring(true)
    setMeasureErr(null)
    try { setMeasured(await measureOrder(order.id)) } catch (e) { setMeasureErr(mktError(e, 'Twasta couldn’t measure the project just now.').message) } finally { setMeasuring(false) }
  }, [order.id])
  useEffect(() => { measure() }, [measure])

  const clean = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean)
  const send = async () => {
    setSending(true)
    try {
      await sendReport(order.id, {
        rag, period_label: period.trim() || undefined, summary: summary.trim() || undefined,
        done: clean(done), next: clean(next),
        needs: needs.filter((n) => n.text.trim()).map((n) => ({ text: n.text.trim(), due: n.due || undefined })),
        risks: risks.filter((r) => r.text.trim()).map((r) => ({ level: r.level, text: r.text.trim() })),
      })
      toast.success(`Report sent to ${order.client.name}`)
      onDone()
    } catch (e) {
      toast.error(mktError(e).message)
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="mkt-panel mkt-panel-accent mkt-col" aria-label="Write a progress report" style={{ gap: 16 }}>
      <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
        <h2>Write a progress report</h2>
        <span className="mkt-faint">Takes about 5 minutes</span>
      </div>

      <fieldset style={{ border: 0, margin: 0, padding: 0 }} className="mkt-col">
        <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>How is it going?</legend>
        <div className="mkt-choices" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          {(Object.keys(RAG) as ProgressReport['rag'][]).map((k) => (
            <button key={k} type="button" aria-pressed={rag === k} className={`mkt-choice${rag === k ? ' on' : ''}`} onClick={() => setRag(k)}>
              <strong><span aria-hidden="true" className={`mkt-pill ${RAG[k].tone}`} style={{ padding: '0 6px', marginRight: 6 }}>●</span>{RAG[k].label}</strong>
              <span className="mkt-muted" style={{ fontSize: 14 }}>{RAG[k].hint}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div className="mkt-field">
          <label htmlFor="rep-period">Period (optional)</label>
          <input id="rep-period" value={period} maxLength={60} placeholder="e.g. Week 3" onChange={(e) => setPeriod(e.target.value)} />
        </div>
        <div className="mkt-field">
          <label htmlFor="rep-summary">In one line</label>
          <input id="rep-summary" value={summary} maxLength={200} placeholder="Screens and data model are done" onChange={(e) => setSummary(e.target.value)} />
        </div>
      </div>

      <div className="mkt-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        <ListEditor id="rep-done" label="Done" items={done} onChange={setDone} placeholder="What you finished" />
        <ListEditor id="rep-next" label="Next" items={next} onChange={setNext} placeholder="What comes next" />
      </div>

      <fieldset style={{ border: 0, margin: 0, padding: 0 }} className="mkt-col">
        <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>Needs from {order.client.name}</legend>
        {needs.map((n, i) => (
          <div key={i} className="mkt-row" style={{ gap: 8, alignItems: 'flex-end' }}>
            <div className="mkt-field" style={{ flex: '1 1 260px' }}>
              <label htmlFor={`need-${i}`}>What you need</label>
              <input id={`need-${i}`} value={n.text} onChange={(e) => setNeeds(needs.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
            </div>
            <div className="mkt-field">
              <label htmlFor={`need-due-${i}`}>Needed by</label>
              <input id={`need-due-${i}`} type="date" value={n.due} onChange={(e) => setNeeds(needs.map((x, j) => (j === i ? { ...x, due: e.target.value } : x)))} />
            </div>
            <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove need ${i + 1}`} onClick={() => setNeeds(needs.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}
        <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setNeeds([...needs, { text: '', due: '' }])}>+ Add a need</button>
      </fieldset>

      <fieldset style={{ border: 0, margin: 0, padding: 0 }} className="mkt-col">
        <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>Risks</legend>
        {risks.map((r, i) => (
          <div key={i} className="mkt-row" style={{ gap: 8, alignItems: 'flex-end' }}>
            <div className="mkt-field">
              <label htmlFor={`risk-lvl-${i}`}>Level</label>
              <select id={`risk-lvl-${i}`} value={r.level} onChange={(e) => setRisks(risks.map((x, j) => (j === i ? { ...x, level: e.target.value as RiskRow['level'] } : x)))}>
                {(Object.keys(RISK_LABEL) as RiskRow['level'][]).map((l) => <option key={l} value={l}>{RISK_LABEL[l]}</option>)}
              </select>
            </div>
            <div className="mkt-field" style={{ flex: '1 1 260px' }}>
              <label htmlFor={`risk-${i}`}>What could go wrong</label>
              <input id={`risk-${i}`} value={r.text} onChange={(e) => setRisks(risks.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
            </div>
            <button type="button" className="btn btn-ghost btn-sm" aria-label={`Remove risk ${i + 1}`} onClick={() => setRisks(risks.filter((_, j) => j !== i))}>Remove</button>
          </div>
        ))}
        <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setRisks([...risks, { level: 'medium', text: '' }])}>+ Add a risk</button>
      </fieldset>

      <div className="mkt-col" style={{ gap: 8 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
          <span className="mkt-muted" style={{ fontSize: 14 }}>Twasta attaches what it measures in your project when you send — right now:</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={measure} disabled={measuring}>{measuring ? 'Measuring…' : 'Measure again'}</button>
        </div>
        {measureErr && <div className="mkt-msg warn">{measureErr}</div>}
        {measured && <MeasuredBlock m={measured} title="What Twasta will attach" />}
      </div>

      <div className="mkt-row">
        <button type="button" className="btn btn-primary" disabled={sending} onClick={send}>{sending ? 'Sending…' : `Send report to ${order.client.name}`}</button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>Not now</button>
      </div>
    </section>
  )
}

